// Send later (U12) on the client: the Scheduled list (cards' replies and composer messages that
// wait for their time) and the owner's clock that every time here is read on.
import { useQuery, type QueryClient } from "@tanstack/react-query";
import type { ScheduledSendView, TimeZoneSetting } from "@shared/api";
import { api, userTimeZone } from "@web/lib/api";
import { settingsKeys, useOwnerTimeZone } from "@web/lib/settingsApi";

/** Under the compose keys, so whatever refreshes drafts refreshes this list too. */
export const scheduledKey = ["compose", "scheduled"] as const;

export const sendLaterApi = {
  scheduled: (signal?: AbortSignal) =>
    api<ScheduledSendView[]>("/api/compose/scheduled", { signal }),
};

export function useScheduledSends() {
  return useQuery({
    queryKey: scheduledKey,
    queryFn: ({ signal }) => sendLaterApi.scheduled(signal),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function invalidateScheduled(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: scheduledKey });
}

const FALLBACK_ZONE = "UTC";

/** The owner's saved zone; this browser's until it loads. */
export function useSendZone(): string {
  return useOwnerTimeZone() ?? userTimeZone() ?? FALLBACK_ZONE;
}

/** The same, outside React (from the settings cache). */
export function sendZoneFrom(queryClient: QueryClient): string {
  const setting = queryClient.getQueryData<TimeZoneSetting>(settingsKeys.timeZone);
  return setting?.timeZone ?? userTimeZone() ?? FALLBACK_ZONE;
}
